import { useState } from 'react';
import { Incident } from '../data/demoData';
import { ApiError, apiFetch } from '../lib/apiClient';
import { notifyIncidentExecutionUpdated } from '../lib/incidentExecutionEvents';

export interface ClosePayload {
  classification: string;
  classificationReason: string;
  classificationComment: string;
  assignee?: string;
}

interface CloseOption {
  label: string;
  classification: string;
  classificationReason: string;
}

const CLOSE_OPTIONS: CloseOption[] = [
  { label: 'Undetermined',                                  classification: 'Undetermined',  classificationReason: '' },
  { label: 'True Positive – Suspicious activity',          classification: 'TruePositive',   classificationReason: 'SuspiciousActivity' },
  { label: 'Benign Positive – Suspicious but expected',    classification: 'BenignPositive', classificationReason: 'SuspiciousButExpected' },
  { label: 'False Positive – Incorrect alert logic',       classification: 'FalsePositive',  classificationReason: 'IncorrectAlertLogic' },
  { label: 'False Positive – Inaccurate data',             classification: 'FalsePositive',  classificationReason: 'InaccurateData' },
];

interface Props {
  incident: Incident;
  customerId?: string;
  onCancel: () => void;
  onClosed: (payload: ClosePayload) => void;
}

export function CloseIncidentDialog({ incident, customerId, onCancel, onClosed }: Props) {
  const [selected, setSelected] = useState<CloseOption>(CLOSE_OPTIONS[0]);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fallbackPrompt, setFallbackPrompt] = useState<string | null>(null);
  const [fallbackAllowed, setFallbackAllowed] = useState(false);

  function isDelegatedFallbackPrompt(e: unknown): e is ApiError {
    return e instanceof ApiError && e.status === 409 && e.body?.["error"] === "delegated_rbac_required";
  }

  async function submitClose(useServicePrincipalFallback = false) {
    setLoading(true);
    setError(null);
    if (!useServicePrincipalFallback) { setFallbackPrompt(null); setFallbackAllowed(false); }

    const payload: ClosePayload = {
      classification: selected.classification,
      classificationReason: selected.classificationReason,
      classificationComment: comment.trim(),
    };

    try {
      const qs = customerId ? `?customerId=${encodeURIComponent(customerId)}` : '';
      const response = await apiFetch(`/incidents/${encodeURIComponent(incident.id)}/close${qs}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...payload,
          armIncidentName: incident.armIncidentName,
          graphIncidentId: incident.graphIncidentId,
          useServicePrincipalFallback,
        }),
      });
      void response;
      notifyIncidentExecutionUpdated(incident.id);
      onClosed(payload);
    } catch (e) {
      if (isDelegatedFallbackPrompt(e)) {
        const msg = typeof e.body["message"] === 'string'
          ? e.body["message"] as string
          : 'Your user identity could not complete this delegated Sentinel action. Do you still wish to proceed using the Service Principal RBAC?';
        setFallbackPrompt(msg);
        setFallbackAllowed(e.body?.["canUseServicePrincipalFallback"] === true);
        setError(null);
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
      setLoading(false);
    }
  }

  async function handleClose() {
    await submitClose(false);
  }

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center px-4"
      onClick={onCancel}
    >
      <div
        className="bg-card rounded-xl shadow-2xl w-full max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Header */}
          <div className="mb-5">
            <h2 className="text-base font-bold text-foreground mb-1">Close Incident</h2>
            <p className="text-xs text-muted-foreground font-mono">
              #{incident.incidentNumber} · {incident.title.length > 60 ? incident.title.slice(0, 60) + '…' : incident.title}
            </p>
          </div>

          {/* Classification */}
          <div className="mb-4">
            <label className="block text-xs font-semibold text-foreground mb-2">
              Classification <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {CLOSE_OPTIONS.map(opt => (
                <label
                  key={opt.classification + opt.classificationReason}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selected === opt
                      ? 'border-primary bg-primary/5 dark:bg-primary/10'
                      : 'border-border hover:border-primary/50 hover:bg-muted/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="classification"
                    checked={selected === opt}
                    onChange={() => setSelected(opt)}
                    className="accent-primary"
                  />
                  <span className="text-sm text-foreground">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Comment */}
          <div className="mb-5">
            <label className="block text-xs font-semibold text-foreground mb-2">
              Classification comment <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Add a comment for why this incident is being closed…"
              rows={3}
              className="w-full rounded-lg border border-border bg-background text-sm text-foreground px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 placeholder:text-muted-foreground/60"
            />
          </div>

          {/* Delegated user RBAC fallback prompt */}
          {fallbackPrompt && (
            <div className="mb-4 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 p-3">
              <div className="text-xs font-semibold text-amber-900 dark:text-amber-200 mb-1">Delegated user action failed</div>
              <div className="text-xs text-amber-800 dark:text-amber-100 mb-3">{fallbackPrompt}</div>
              <div className="flex gap-2">
                {fallbackAllowed && (
                  <button
                    type="button"
                    onClick={() => submitClose(true)}
                    disabled={loading}
                    className="px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold disabled:opacity-50"
                  >
                    Yes, use Service Principal
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setFallbackPrompt(null); setFallbackAllowed(false); }}
                  disabled={loading}
                  className="px-3 py-1.5 rounded border border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-100 text-xs font-semibold hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-50"
                >
                  No, cancel
                </button>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mb-4 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              disabled={loading}
              className="flex-1 py-2.5 rounded-lg border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleClose}
              disabled={loading}
              className="flex-1 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block" />
                  Closing…
                </>
              ) : 'Close Incident'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
